import React, { useState, useEffect } from 'react';
import { Menu, Avatar, Typography, Tooltip, Button, Dropdown, theme } from 'antd';
import {
  SafetyCertificateOutlined,
  ScanOutlined,
  DashboardOutlined,
  BankOutlined,
  GlobalOutlined,
  SettingOutlined,
  UserOutlined,
  TeamOutlined,
  CalendarOutlined,
  FileTextOutlined,
  FileProtectOutlined,
  CheckSquareOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HeartOutlined,
  DatabaseOutlined,
  BookOutlined,
  FundOutlined,
  LockOutlined,
  MailOutlined,
  CreditCardOutlined,
  RobotOutlined,
  MobileOutlined,
  CommentOutlined,
  VideoCameraOutlined,
  NotificationOutlined,
  MedicineBoxOutlined,
  FieldTimeOutlined,
  SwapOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { UserRole, SidebarMenuItem } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrentAgency } from '../features/agencies/api/agenciesApi';
import { hasPermission } from '../utils/permissions';
import { agencyMenuConfigApi } from '../services/agencyMenuConfigApi';
import { BillingService } from '../services/billingService';
import versionData from '../version.json';
import { AgencyMembershipList } from './AgencySwitcher';
import { FEATURE_PREVIEWS, featurePreviewPath } from '../features/feature-preview/registry';

const { Text } = Typography;

const SidebarContainer = styled.div<{ 
  $width: number; 
  $isMobile: boolean; 
  $isVisible: boolean;
  $isMinimized: boolean;
}>`
  height: calc(100vh - var(--impersonation-banner-height, 0px));
  width: ${props => props.$width}px;
  background: #001529;
  position: fixed;
  left: ${props => {
    if (props.$isMobile) {
      return props.$isVisible ? '0' : '-250px';
    }
    return props.$isVisible ? '0' : `-${props.$width}px`;
  }};
  top: var(--impersonation-banner-height, 0px);
  overflow-y: auto;
  overflow-x: hidden;
  z-index: ${props => props.$isMobile ? 1001 : 1000};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  /* Hide scrollbar for cleaner experience */
  scrollbar-width: thin;
  scrollbar-color: #ffffff20 transparent;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ffffff20;
    border-radius: 2px;
  }

  /* Hover effect for unpinned sidebar */
  ${props => !props.$isMobile && !props.$isVisible && `
    &:hover {
      left: 0;
    }
  `}
`;

const TopControlsSection = styled.div<{ $isMinimized: boolean; $isMobile: boolean }>`
  padding: 8px;
  display: ${props => props.$isMobile ? 'none' : 'flex'};
  justify-content: flex-end;
  border-bottom: 1px solid #1f1f1f;
`;

const UserSection = styled.div<{ $isMinimized: boolean; $isMobile: boolean }>`
  padding: ${props => props.$isMinimized && !props.$isMobile ? '12px 8px' : '20px'};
  border-bottom: 1px solid #1f1f1f;
  text-align: center;
  transition: padding 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const UserInfo = styled.div<{ $isMinimized: boolean; $isMobile: boolean }>`
  margin-top: 12px;
  display: ${props => props.$isMinimized && !props.$isMobile ? 'none' : 'block'};
  transition: opacity 0.3s ease;
`;

const StyledMenu = styled(Menu)<{ $primaryColor: string; $isMinimized: boolean; $isMobile: boolean }>`
  border: none;
  margin-top: 20px;
  background: transparent;

  .ant-menu-item {
    margin: 0;
    padding: 0 24px;
    height: 40px;
    line-height: 40px;
    display: flex;
    align-items: center;
    color: white !important;
    
    .ant-menu-item-icon {
      font-size: 16px;
      min-width: 16px;
      margin-right: 12px;
      display: inline-flex;
      align-items: center;
      color: white !important;
    }
    
    .ant-menu-title-content {
      flex: 1;
      display: inline-flex;
      align-items: center;
      color: white !important;
    }

    span {
      color: white !important;
    }
  }

  .ant-menu-item-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: rgba(255, 255, 255, 0.5) !important;
    
    .ant-menu-item-icon {
      color: rgba(255, 255, 255, 0.5) !important;
    }
    
    .ant-menu-title-content {
      color: rgba(255, 255, 255, 0.5) !important;
    }
    
    span {
      color: rgba(255, 255, 255, 0.5) !important;
    }
  }

  .ant-menu-item:hover:not(.ant-menu-item-disabled) {
    background-color: ${props => props.$primaryColor};
    color: white !important;
    
    .ant-menu-item-icon {
      color: white !important;
    }
    
    .ant-menu-title-content {
      color: white !important;
    }
    
    span {
      color: white !important;
    }
  }

  .ant-menu-item-selected {
    background-color: ${props => props.$primaryColor};
    color: white !important;
    
    .ant-menu-item-icon {
      color: white !important;
    }
    
    .ant-menu-title-content {
      color: white !important;
    }
    
    span {
      color: white !important;
    }
  }

  @media (max-width: 768px) {
    .ant-menu-item {
      padding: 0 20px;
      height: 44px;
      line-height: 44px;
    }
  }
`;

const VersionBadge = styled.div`
  text-align: center;
  padding-top: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  font-family: monospace;
  letter-spacing: 0.5px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
`;

const ControlsSection = styled.div<{ $isMinimized: boolean; $isMobile: boolean }>`
  padding: ${props => props.$isMinimized && !props.$isMobile ? '8px' : '16px'};
  border-top: 1px solid #1f1f1f;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
`;

const CollapseButton = styled(Button)<{ $isMinimized: boolean; $isMobile: boolean }>`
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  display: ${props => props.$isMobile ? 'none' : 'flex'};
  align-items: center;
  justify-content: center;
  width: ${props => props.$isMinimized ? '28px' : 'auto'};
  height: 28px;
  font-size: 12px;
  
  &:hover, &:focus {
    background: rgba(255, 255, 255, 0.2) !important;
    color: white !important;
    border-color: transparent !important;
  }
`;

const LogoutButton = styled.div<{ $isMinimized: boolean; $isMobile: boolean }>`
  padding: ${props => props.$isMinimized && !props.$isMobile ? '8px' : '10px 16px'};
  background: var(--primary-color, #f24b6a);
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  color: white;
  font-weight: 500;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: ${props => props.$isMinimized && !props.$isMobile ? 'none' : '1'};
  width: ${props => props.$isMinimized && !props.$isMobile ? '36px' : 'auto'};
  margin-top: ${props => props.$isMinimized && !props.$isMobile ? '8px' : '0'};

  &:hover {
    background: var(--primary-color, #f24b6a);
    filter: brightness(1.1);
  }
  
  .anticon {
    margin-right: ${props => props.$isMinimized && !props.$isMobile ? '0' : '8px'};
  }
  
  span:not(.anticon) {
    display: ${props => props.$isMinimized && !props.$isMobile ? 'none' : 'inline'};
  }
`;

interface SidebarProps {
  width: number;
  isMobile: boolean;
  isVisible: boolean;
  isMinimized: boolean;
  onClose?: () => void;
  onToggleCollapse?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  width,
  isMobile,
  isVisible,
  isMinimized,
  onClose,
  onToggleCollapse 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated, switchAgency } = useAuth();
  const { t } = useLanguage();
  const { primaryColor } = useTheme();
  const { token: antdToken } = theme.useToken();
  // Same login in several agencies: the logo becomes a switcher (Gmail-style).
  const canSwitchAgency = (user?.memberships?.length || 0) > 1;
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // Get current agency data for logo
  const { data: agency } = useCurrentAgency(isAuthenticated && !!user?.agencyId);

  // Billing features that require a paid plan → menu key mapping
  const BILLING_FEATURE_MENU: Record<string, string> = {
    invoices: 'invoices',
    matching: 'matching',
    vorlagen: 'templates',
    templates: 'templates', // legacy alias
    pflegedoku: 'pflegedoku',
  };

  // Load agency menu config to know which features are disabled
  const [disabledMenuKeys, setDisabledMenuKeys] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (user?.agencyId && user.role !== UserRole.SUPER_ADMIN) {
      agencyMenuConfigApi.getAgencyMenuConfig(user.agencyId)
        .then(config => {
          const disabled = new Set(
            config.menuPermissions.filter(p => !p.enabled).map(p => p.key)
          );
          setDisabledMenuKeys(disabled);
        })
        .catch(() => {}); // non-critical
    }
  }, [user?.agencyId, user?.role]);

  // HerzensApp module activation (super-admin controlled, off by default).
  // Gates the agency-side "HerzensApp" menu item.
  const [herzensappActive, setHerzensappActive] = useState(false);

  // GAV-Zeiterfassung (Schweiz): pro Agentur freigeschaltet, sonst gibt es
  // den Menuepunkt nicht. Derselbe Ansatz wie beim HerzensApp-Modul.
  const [gavActive, setGavActive] = useState(false);
  useEffect(() => {
    const appRoles = [
      UserRole.AGENCY_ADMIN,
      UserRole.STAFF,
      UserRole.NURSE,
      UserRole.CAREGIVER,
      UserRole.FAMILY,
    ];
    if (user?.agencyId && appRoles.includes(user.role)) {
      import('../services/herzensappApi')
        .then(({ herzensappApi }) => herzensappApi.getStatus())
        .then((status) => setHerzensappActive(status.active))
        .catch(() => setHerzensappActive(false)); // non-critical
    }
  }, [user?.agencyId, user?.role]);

  useEffect(() => {
    const gavRoles = [
      UserRole.AGENCY_ADMIN,
      UserRole.STAFF,
      UserRole.CAREGIVER,
      UserRole.NURSE,
    ];
    if (user?.agencyId && gavRoles.includes(user.role)) {
      import('../services/gavApi')
        .then(({ gavApi }) => gavApi.getStatus())
        .then((status) => setGavActive(!!status.enabled))
        .catch(() => setGavActive(false)); // non-critical
    }
  }, [user?.agencyId, user?.role]);

  // Lock menu items for billing features not included in the agency's plan
  const [billingLockedKeys, setBillingLockedKeys] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (user?.agencyId && user.role !== UserRole.SUPER_ADMIN) {
      BillingService.getBillingInfo()
        .then(billing => {
          if (!billing || billing.isExempt || billing.status === 'pending') return;
          const enabled = new Set(billing.enabledFeatures || []);
          // Group: menuKey → [features that map to it]
          const menuKeyFeatures: Record<string, string[]> = {};
          Object.entries(BILLING_FEATURE_MENU).forEach(([feature, menuKey]) => {
            if (!menuKeyFeatures[menuKey]) menuKeyFeatures[menuKey] = [];
            menuKeyFeatures[menuKey].push(feature);
          });
          // Lock a menu key only if none of its features are enabled
          const locked = new Set<string>();
          Object.entries(menuKeyFeatures).forEach(([menuKey, features]) => {
            if (!features.some(f => enabled.has(f))) locked.add(menuKey);
          });
          setBillingLockedKeys(locked);
        })
        .catch(() => {}); // non-critical
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.agencyId, user?.role]);

  // Define all possible menu items with their permission requirements
  const getAllMenuItems = (): SidebarMenuItem[] => {
    return [
      {
        key: 'dashboard',
        label: t('sidebar.dashboard'),
        icon: <DashboardOutlined />,
        path: '/dashboard',
        roles: [UserRole.SUPER_ADMIN, UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'dashboard',
      },
      {
        key: 'admin-dashboard',
        label: t('sidebar.adminDashboard'),
        icon: <FundOutlined />,
        path: '/admin-dashboard',
        roles: [UserRole.SUPER_ADMIN],
        permission: 'agencies',
      },
      {
        key: 'agencies',
        label: t('sidebar.agencies'),
        icon: <BankOutlined />,
        path: '/agencies',
        roles: [UserRole.SUPER_ADMIN],
        permission: 'agencies',
      },
      {
        key: 'agency-menu-config',
        label: t('sidebar.agencyMenuConfig'),
        icon: <SettingOutlined />,
        path: '/agency-menu-config',
        roles: [UserRole.SUPER_ADMIN],
        permission: 'agencies',
      },
      {
        key: 'admin-billing',
        label: t('sidebar.adminBilling'),
        icon: <CreditCardOutlined />,
        path: '/admin/billing',
        roles: [UserRole.SUPER_ADMIN],
        permission: 'agencies',
      },
      {
        key: 'admin-email-triage',
        label: t('sidebar.adminEmailTriage'),
        icon: <RobotOutlined />,
        path: '/admin/email-triage',
        roles: [UserRole.SUPER_ADMIN],
        permission: 'agencies',
      },
      {
        key: 'admin-herzensapp',
        label: t('sidebar.adminHerzensapp'),
        icon: <MobileOutlined />,
        path: '/admin/herzensapp',
        roles: [UserRole.SUPER_ADMIN],
        permission: 'agencies',
      },
      {
        key: 'admin-feedback',
        label: t('sidebar.adminFeedback'),
        icon: <CommentOutlined />,
        path: '/admin/feedback',
        roles: [UserRole.SUPER_ADMIN],
        permission: 'agencies',
      },
      {
        key: 'admin-newsletter',
        label: t('sidebar.adminNewsletter'),
        icon: <NotificationOutlined />,
        path: '/admin/newsletter',
        roles: [UserRole.SUPER_ADMIN],
        permission: 'agencies',
      },
      {
        key: 'admin-document-ocr',
        label: t('sidebar.adminDocumentOcr'),
        icon: <ScanOutlined />,
        path: '/admin/document-ocr',
        roles: [UserRole.SUPER_ADMIN],
        permission: 'agencies',
      },
      {
        key: 'admin-gewerbestatus',
        label: t('sidebar.adminGewerbestatus'),
        icon: <SafetyCertificateOutlined />,
        path: '/admin/gewerbestatus',
        roles: [UserRole.SUPER_ADMIN],
        permission: 'agencies',
      },
      {
        key: 'admin-testimonials',
        label: t('sidebar.adminTestimonials'),
        icon: <VideoCameraOutlined />,
        path: '/admin/testimonials',
        roles: [UserRole.SUPER_ADMIN],
        permission: 'agencies',
      },
      {
        key: 'herzensapp',
        label: t('sidebar.herzensapp'),
        icon: <MobileOutlined />,
        path: '/documents',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF, UserRole.NURSE, UserRole.CAREGIVER, UserRole.FAMILY],
      },
      {
        key: 'gav',
        label: t('sidebar.gav'),
        icon: <FieldTimeOutlined />,
        path: '/zeiterfassung',
        roles: [
          UserRole.AGENCY_ADMIN,
          UserRole.STAFF,
          UserRole.CAREGIVER,
          UserRole.NURSE,
        ],
      },
      {
        key: 'dgkp',
        label: t('sidebar.dgkp'),
        icon: <MedicineBoxOutlined />,
        path: '/dgkp',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'staff',
      },
      {
        key: 'patients',
        label: t('sidebar.patients'),
        icon: <UserOutlined />,
        path: '/patients',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'patients',
      },
      {
        key: 'caregivers',
        label: t('sidebar.caregivers'),
        icon: <TeamOutlined />,
        path: '/caregivers',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'caregivers',
      },
      {
        key: 'postingAgencies',
        label: t('sidebar.postingAgencies'),
        icon: <GlobalOutlined />,
        path: '/posting-agencies',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'caregivers',
      },
      {
        key: 'matching',
        label: t('sidebar.matching'),
        icon: <HeartOutlined />,
        path: '/matching',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'matching',
      },
      {
        key: 'shifts',
        label: t('sidebar.shifts'),
        icon: <CalendarOutlined />,
        path: '/shifts',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'shifts',
      },
      {
        key: 'checklist',
        label: t('sidebar.checklist'),
        icon: <CheckSquareOutlined />,
        path: '/shifts?view=checklist',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'shifts',
      },
      {
        key: 'invoices',
        label: t('sidebar.invoices'),
        icon: <FileTextOutlined />,
        path: '/invoices',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'invoices',
      },
      {
        key: 'emails',
        label: t('sidebar.emails'),
        icon: <MailOutlined />,
        path: '/emails',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'emails',
      },
      {
        key: 'email-triage',
        label: t('sidebar.emailTriage'),
        icon: <RobotOutlined />,
        path: '/email-triage',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'emails',
      },
      {
        key: 'templates',
        label: t('sidebar.templates'),
        icon: <FileProtectOutlined />,
        path: '/templates',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'templates',
      },
      {
        key: 'tasks',
        label: t('sidebar.tasks'),
        icon: <CheckSquareOutlined />,
        path: '/tasks',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'tasks',
      },
      {
        key: 'knowledge',
        label: t('sidebar.knowledge'),
        icon: <BookOutlined />,
        path: '/knowledge',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'knowledge',
      },
      {
        key: 'files',
        label: t('sidebar.files'),
        icon: <FileTextOutlined />,
        path: '/files',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'files',
      },
      {
        key: 'staff',
        label: t('sidebar.staff'),
        icon: <TeamOutlined />,
        path: '/staff',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'staff',
      },
      {
        key: 'settings',
        label: t('sidebar.settings'),
        icon: <SettingOutlined />,
        path: '/settings',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'settings',
      },
      {
        key: 'billing',
        label: t('sidebar.billing'),
        icon: <CreditCardOutlined />,
        path: '/billing',
        roles: [UserRole.AGENCY_ADMIN, UserRole.STAFF],
        permission: 'billing',
      },
      {
        // Handbuch: every logged-in user, no permission — the server decides
        // which chapters each reader gets. Kept last: its path is a prefix of
        // /documents, and the selected entry is the first path match.
        key: 'docs',
        label: t('sidebar.docs'),
        icon: <ReadOutlined />,
        path: '/doc',
        roles: [
          UserRole.SUPER_ADMIN,
          UserRole.AGENCY_ADMIN,
          UserRole.STAFF,
          UserRole.NURSE,
          UserRole.CAREGIVER,
          UserRole.FAMILY,
        ],
      },
    ];
  };

  const handleMenuClick = (item: SidebarMenuItem) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Menu item clicked:', item.key, 'path:', item.path);
    }
    navigate(item.path);
    
    // Close sidebar on mobile after navigation
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (!user) {
    return null;
  }

  // Filter menu items based on user permissions
  const allMenuItems = getAllMenuItems();
  const isOfficeUser = user.role === UserRole.AGENCY_ADMIN || user.role === UserRole.STAFF;
  const menuItems = allMenuItems.filter(item => {
    // Check if user's role is allowed
    if (!item.roles.includes(user.role)) {
      return false;
    }

    // HerzensApp: only show the agency-side item when the module is activated
    // Inactive for office users it is still listed, as a preview (see below).
    if (item.key === 'herzensapp' && !herzensappActive && !isOfficeUser) {
      return false;
    }

    // Entsendeagenturen: nur fuer deutsche Agenturen, und nur solange der
    // Super-Admin den Punkt in /agency-menu-config nicht abgeschaltet hat
    // (Backend sperrt dann auch die API).
    if (item.key === 'postingAgencies' && (agency?.country !== 'DE' || disabledMenuKeys.has('postingAgencies'))) {
      return false;
    }

    // GAV-Zeiterfassung: nur fuer freigeschaltete (Schweizer) Agenturen
    if (item.key === 'gav' && !gavActive) {
      return false;
    }

    // Pflegekräfte: the super admin switches this entry off per agency in
    // /agency-menu-config. Unlike the billing-locked entries it is hidden
    // rather than shown as locked — there is nothing to unlock, the agency
    // simply does not use it.
    if (item.key === 'dgkp' && disabledMenuKeys.has('dgkp')) {
      return false;
    }

    // Check permission if specified
    if (item.permission) {
      return hasPermission(user, item.permission);
    }

    return true;
  });

  // Determine selected menu item based on current location
  const selectedKey = (() => {
    // Special case: check if we're on the checklist view
    if (location.pathname === '/shifts' && location.search.includes('view=checklist')) {
      return 'checklist';
    }

    const preview = location.pathname.match(/^\/feature-preview\/([^/]+)/);
    if (preview) return preview[1];

    // Find matching menu item by path
    const matchedItem = menuItems.find(item => {
      // For items with query parameters, match exact path
      if (item.path.includes('?')) {
        const [path] = item.path.split('?');
        return location.pathname === path;
      }
      // For regular items, check if path starts with item path
      return location.pathname.startsWith(item.path);
    });

    return matchedItem?.key || 'dashboard';
  })();

  // Convert menu items to Antd Menu items format
  const antdMenuItems = menuItems.map((item) => {
    const isAdminDisabled = disabledMenuKeys.has(item.key);
    const isBillingLocked = billingLockedKeys.has(item.key);
    const isInactiveModule = item.key === 'herzensapp' && !herzensappActive;
    // Not unlocked, but a preview exists: keep it clickable and show the demo.
    const previewOnly = !!FEATURE_PREVIEWS[item.key] && (isAdminDisabled || isBillingLocked || isInactiveModule);
    const isDisabled = (isAdminDisabled || isBillingLocked) && !previewOnly;

    if (previewOnly) {
      const previewLabel = isMinimized && !isMobile ? null : (
        <Tooltip title={t('featurePreview.sidebarTooltip')} placement="right">
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {item.label}
            <span style={{ fontSize: 10, padding: '0 5px', borderRadius: 4, border: '1px solid currentColor', opacity: 0.75, lineHeight: '16px' }}>
              {t('featurePreview.previewTag')}
            </span>
          </span>
        </Tooltip>
      );
      return {
        key: item.key,
        icon: item.icon,
        label: previewLabel,
        onClick: () => handleMenuClick({ ...item, path: featurePreviewPath(item.key) }),
        title: isMinimized && !isMobile ? item.label : undefined,
        disabled: false,
      };
    }

    const lockTooltip = isBillingLocked
      ? t('sidebar.lockedByBilling')
      : t('sidebar.lockedByAdmin');

    const labelContent = isDisabled && !isMinimized ? (
      <Tooltip title={lockTooltip} placement="right">
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {item.label}
          <LockOutlined style={{ fontSize: 11, opacity: 0.7 }} />
        </span>
      </Tooltip>
    ) : (isMinimized && !isMobile ? null : item.label);

    return {
      key: item.key,
      icon: item.icon,
      label: labelContent,
      onClick: isDisabled ? undefined : () => handleMenuClick(item),
      title: isMinimized && !isMobile ? item.label : undefined,
      disabled: isDisabled,
    };
  });

  // Debug logging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('👤 User permissions:', user.permissions);
    console.log('📋 Available menu items:', menuItems.map(item => item.key));
    console.log('🏢 Agency data in Sidebar:', agency);
    console.log('🖼️ Logo URL:', agency?.logoUrl);
  }

  return (
    <SidebarContainer 
      $width={width}
      $isMobile={isMobile}
      $isVisible={isVisible}
      $isMinimized={isMinimized}
    >
      <TopControlsSection $isMinimized={isMinimized} $isMobile={isMobile}>
        <Tooltip title={isMinimized ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')} placement="right">
          <CollapseButton 
            $isMinimized={isMinimized} 
            $isMobile={isMobile}
            onClick={onToggleCollapse}
            icon={isMinimized ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          >
            {!isMinimized && (isMinimized ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar'))}
          </CollapseButton>
        </Tooltip>
      </TopControlsSection>

      <Dropdown
        open={canSwitchAgency && switcherOpen}
        onOpenChange={(o) => setSwitcherOpen(canSwitchAgency && o)}
        trigger={['click']}
        placement="bottomLeft"
        disabled={!canSwitchAgency}
        dropdownRender={() => (
          <div
            style={{
              width: 300,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: '60vh',
              overflowY: 'auto',
              padding: 8,
              background: antdToken.colorBgElevated,
              borderRadius: antdToken.borderRadiusLG,
              boxShadow: antdToken.boxShadowSecondary,
            }}
          >
            <Text type="secondary" style={{ display: 'block', padding: '4px 12px 8px', fontSize: 12 }}>
              {t('agencySwitch.switchTo')}
            </Text>
            <AgencyMembershipList
              memberships={user.memberships || []}
              currentId={user.id}
              onSelect={(m) => { setSwitcherOpen(false); switchAgency(m.id); }}
            />
          </div>
        )}
      >
      <UserSection
        $isMinimized={isMinimized}
        $isMobile={isMobile}
        {...(canSwitchAgency ? {
          role: 'button',
          tabIndex: 0,
          'aria-haspopup': 'listbox',
          'aria-expanded': switcherOpen,
          title: t('agencySwitch.switchTo'),
          style: { cursor: 'pointer' },
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSwitcherOpen((o) => !o); }
          },
        } : {})}
      >
        <Avatar 
          size={isMinimized && !isMobile ? 32 : 64} 
          src={agency?.logoUrl}
          icon={!agency?.logoUrl ? <BankOutlined /> : undefined}
          onError={() => {
            if (process.env.NODE_ENV === 'development') {
              console.error('❌ Avatar image failed to load');
              console.error('❌ Failed URL:', agency?.logoUrl);
            }
            return false;
          }}
          style={{ 
            backgroundColor: agency?.logoUrl ? 'transparent' : primaryColor,
            border: agency?.logoUrl ? '2px solid #f0f0f0' : 'none'
          }}
        />
        <UserInfo $isMinimized={isMinimized} $isMobile={isMobile}>
          <Text style={{ color: 'white', display: 'block', fontWeight: 'bold' }}>
            {agency?.name || `${user.firstName} ${user.lastName}`}
          </Text>
          <Text style={{ color: '#bfbfbf', fontSize: '12px' }}>
            {t(`sidebar.roles.${user.role}`)}
            {canSwitchAgency && <SwapOutlined style={{ marginLeft: 6 }} aria-hidden />}
          </Text>
        </UserInfo>
      </UserSection>
      </Dropdown>

      <StyledMenu
        theme="dark"
        mode="vertical"
        selectedKeys={[selectedKey]}
        items={antdMenuItems}
        $primaryColor={primaryColor}
        $isMinimized={isMinimized}
        $isMobile={isMobile}
        inlineCollapsed={isMinimized && !isMobile}
      />

      <ControlsSection $isMinimized={isMinimized} $isMobile={isMobile}>
        <Tooltip title={t('sidebar.logout')} placement="top">
          <LogoutButton
            onClick={handleLogout}
            $isMinimized={isMinimized}
            $isMobile={isMobile}
          >
            <LogoutOutlined />
            <span>{t('sidebar.logout')}</span>
          </LogoutButton>
        </Tooltip>
        {(!isMinimized || isMobile) && (
          <VersionBadge>v{versionData.version}</VersionBadge>
        )}
      </ControlsSection>
    </SidebarContainer>
  );
};

export default Sidebar; 